/** The authenticated Insights page on the production route. */
import { useAppBridge } from '@shopify/app-bridge-react'
import type { FC } from 'react'
import { insightsLoadingCopy as copy } from '@testfeed/product-language'
import { useInsights } from '../../hooks/useInsights'
import { ShopifyErrorBoundary } from '../ShopifyErrorBoundary'
import { InsightsDelivery } from './InsightsDelivery'
import { InsightsLoading } from './InsightsLoading'
import { InsightsReadinessState } from './InsightsReadinessState'
import { StoreKnowledgePanel } from './StoreKnowledgePanel'
import { SavedResearchCards } from './SavedResearchCards'
import type { TestInsightSetup } from './InsightSetupsSection'
import { trackShopifyRefetch } from '../../shopifyUiTracking'

export const InsightsRoute: FC<{
  onTestSetup?: TestInsightSetup
}> = ({ onTestSetup }) => {
  const shopify = useAppBridge()
  const query = useInsights(shopify)
  const onRetry = () => {
    if (query.isRefreshing) return
    void trackShopifyRefetch(shopify, query.reload)
  }
  let refreshNotice = null
  if (query.isRefreshing)
    refreshNotice = (
      <div role="status" aria-label={copy.refreshing}>
        <s-paragraph>{copy.refreshing}</s-paragraph>
      </div>
    )
  else if (query.error && query.data?.state === 'ready')
    refreshNotice = (
      <s-section>
        <s-stack gap="base">
          <s-banner tone="warning">{copy.refreshError}</s-banner>
          <s-button onClick={onRetry}>{copy.retry}</s-button>
        </s-stack>
      </s-section>
    )
  let content
  if (query.data?.state === 'ready' && query.lifecycle.state.status === 'ready') {
    content = (
      <ShopifyErrorBoundary
        key={query.data.diagnosticAttempt?.attemptId}
        shopify={shopify}
        diagnosticAttempt={query.data.diagnosticAttempt}
        isDiagnosticActive={query.isDiagnosticActive}
      >
        <InsightsDelivery data={query.data} onTestSetup={onTestSetup} lifecycle={query.lifecycle} />
      </ShopifyErrorBoundary>
    )
  } else if (query.isLoading || query.data?.state === 'ready') {
    content = <InsightsLoading />
  } else if (query.error) {
    content = (
      <s-section>
        <s-banner tone="critical">
          {copy.error}
          <s-button slot="secondary-actions" onClick={onRetry}>
            {copy.retry}
          </s-button>
        </s-banner>
      </s-section>
    )
  } else if (query.data && 'retryable' in query.data) {
    content = (
      <InsightsReadinessState
        data={query.data}
        polling={query.isPolling}
        checking={query.isRefreshing}
        onRetry={onRetry}
      />
    )
  } else {
    content = (
      <s-section>
        <s-stack gap="base">
          <s-paragraph>{copy.unavailable}</s-paragraph>
          <s-button onClick={onRetry}>{copy.retry}</s-button>
        </s-stack>
      </s-section>
    )
  }
  return (
    <s-page heading={copy.pageTitle} inlineSize="large">
      <s-stack gap="base">
        {refreshNotice}
        {content}
        <SavedResearchCards shopify={shopify} />
        <StoreKnowledgePanel shopify={shopify} />
      </s-stack>
    </s-page>
  )
}
